/**
 * Content Engine Chat Handoff
 *
 * Stores browser-to-Chat Content Engine prompts until the Chat composer loads.
 *
 * Primary responsibilities:
 * - Queue a Content Engine prompt for Losi Chat
 * - Consume the prompt after it is drafted or sent
 * - Notify Chat when a handoff is queued in the same tab
 *
 * Dependencies:
 * - Browser localStorage
 */

export const CONTENT_ENGINE_HANDOFF_STORAGE_KEY = 'losi.content-engine.chat-handoff.v1'
export const CONTENT_ENGINE_HANDOFF_EVENT = 'losi:content-engine-chat-handoff'

export type ContentEngineChatHandoffPayload = {
  id: string
  message: string
  workspaceId: string
  autoSend: boolean
  createChat: boolean
  receivedAt: number
}

function sanitizeString(value: unknown) {
  return typeof value === 'string' ? value.trim() : ''
}

function sanitizeBoolean(value: unknown, fallback = false) {
  if (typeof value === 'boolean') return value
  if (value === 'true') return true
  if (value === 'false') return false
  return fallback
}

function normalizeHandoffPayload(value: unknown): ContentEngineChatHandoffPayload | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const payload = value as Record<string, unknown>
  const message = sanitizeString(payload.message)
  const workspaceId = sanitizeString(payload.workspaceId || payload.workspace_id)

  if (!message || !workspaceId) {
    return null
  }

  return {
    id: sanitizeString(payload.id) || crypto.randomUUID(),
    message,
    workspaceId,
    autoSend: sanitizeBoolean(payload.autoSend, true),
    createChat: sanitizeBoolean(payload.createChat, true),
    receivedAt: Number.isFinite(Number(payload.receivedAt))
      ? Number(payload.receivedAt)
      : Date.now()
  }
}

function readQueue() {
  if (typeof window === 'undefined') {
    return []
  }

  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(CONTENT_ENGINE_HANDOFF_STORAGE_KEY) || '[]'
    )

    return Array.isArray(parsed)
      ? parsed
          .map(normalizeHandoffPayload)
          .filter((payload): payload is ContentEngineChatHandoffPayload => Boolean(payload))
      : []
  } catch {
    return []
  }
}

function writeQueue(queue: ContentEngineChatHandoffPayload[]) {
  if (typeof window === 'undefined') {
    return
  }

  if (queue.length === 0) {
    window.localStorage.removeItem(CONTENT_ENGINE_HANDOFF_STORAGE_KEY)
    return
  }

  window.localStorage.setItem(CONTENT_ENGINE_HANDOFF_STORAGE_KEY, JSON.stringify(queue))
}

function dispatchHandoffEvent() {
  if (typeof window === 'undefined') {
    return
  }

  window.dispatchEvent(new CustomEvent(CONTENT_ENGINE_HANDOFF_EVENT))
}

export function writePendingContentEngineChatHandoff(
  payload: Omit<ContentEngineChatHandoffPayload, 'id' | 'receivedAt'> & {
    id?: string
    receivedAt?: number
  }
) {
  const normalizedPayload = normalizeHandoffPayload(payload)

  if (!normalizedPayload) {
    return null
  }

  const queue = readQueue().filter((item) => item.id !== normalizedPayload.id)
  queue.push({
    ...normalizedPayload,
    receivedAt: Date.now()
  })

  writeQueue(queue)
  dispatchHandoffEvent()

  return normalizedPayload.id
}

export function readPendingContentEngineChatHandoff() {
  return readQueue()[0] || null
}

export function consumePendingContentEngineChatHandoff(id = '') {
  const queue = readQueue()

  if (queue.length === 0) {
    return
  }

  const nextQueue = id
    ? queue.filter((payload, index) => !(index === 0 && payload.id === id))
    : queue.slice(1)

  writeQueue(nextQueue)

  if (nextQueue.length > 0) {
    dispatchHandoffEvent()
  }
}

export function onPendingContentEngineChatHandoff(callback: () => void) {
  if (typeof window === 'undefined') {
    return () => {}
  }

  window.addEventListener(CONTENT_ENGINE_HANDOFF_EVENT, callback)

  return () => {
    window.removeEventListener(CONTENT_ENGINE_HANDOFF_EVENT, callback)
  }
}
